import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import i18n from '@/utils/i18n';
import OfflineModeIcon from '@assets/icons/offline-mode.svg';
import PencilIcon from '@assets/icons/pencil-thin.svg';
import PlusIcon from '@assets/icons/plus.svg';
import PrinterEmptyIcon from '@assets/icons/printer-empty.svg';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Analytics } from '@/utils/analytics';
import {
  BORDER_RADIUS_SMALL,
  FONTS,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { Button } from '@/components/button';
import { CircledIcon } from '@/components/circled-icon';
import { createStyles } from '@/contexts/theme-context';
import { CreateTagPrintBatchActionSheet } from '@/components/tag-print-batch/create-tag-print-batch-action-sheet';
import { CustomTagTemplateActionSheet } from '@/components/tag-print-batch/custom-tag-template-action-sheet';
import { ListOption } from '@/components/list-item';
import { ORDER_GUIDE_ROUTES } from '@/navigators/routes';
import { OrderGuideStackParams } from '@/navigators/types';
import { PageContainer } from '@/components/page-container';
import {
  PrintTagBatchListFieldsFragment,
  TagPrint_BatchListQuery,
  TagPrint_BatchListQueryVariables,
} from '@/types';
import { RecyclerList } from '@/components/recycler-list';
import { StackNavigationProp } from '@react-navigation/stack';
import { StatusBar, Text, TouchableOpacity, View } from 'react-native';
import { TAG_PRINT_BATCH_LIST_QUERY } from '@/components/tag-print-batch-queries';
import { TagPrintBatchListItem } from '@/components/tag-print-batch/tag-print-batch-list-item';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useNavigation } from '@react-navigation/native';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineTagBatchPrint } from '@/hooks/use-offline-data';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

// Screen also exists on the catalog stack but only reference
// OrderGuideStack for type simplicity
type Navigation = StackNavigationProp<
  OrderGuideStackParams,
  ORDER_GUIDE_ROUTES.TagPrintBatch
>;

type TagPrintBatchOption = ListOption<PrintTagBatchListFieldsFragment>;

export const TagPrintBatchScreen: React.FC = () => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const isOfflineModeActive = useOfflineModeActive();

  const { bottom } = useSafeAreaInsets();
  const { goBack } = useNavigation<Navigation>();
  const [styles, theme] = useStyles();

  const [showAddBatchSheet, setShowAddBatchSheet] = useState(false);
  const [showCustomTagSheet, setShowCustomTagSheet] = useState(false);

  const customTagPrintingEnabled = useDynamicConfigValue(
    'tag_printing_config',
  )?.custom_tag_printing_enabled;

  const { data: onlineTagPrintBatchListData, loading } = useWrappedQuery<
    TagPrint_BatchListQuery,
    TagPrint_BatchListQueryVariables
  >(TAG_PRINT_BATCH_LIST_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: isOfflineModeActive,
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  const {
    tagBatchPrintData: offlineTagBatchPrintData,
    updateTagPrintBatchData,
  } = useOfflineTagBatchPrint(restaurantUUID, supplierUUID);

  const tagPrintBatches = useMemo(
    () =>
      isOfflineModeActive
        ? (Object.values(offlineTagBatchPrintData?.batches ?? {}) ?? [])
        : (onlineTagPrintBatchListData?.print_tag_batches ?? []),
    [
      isOfflineModeActive,
      offlineTagBatchPrintData?.batches,
      onlineTagPrintBatchListData?.print_tag_batches,
    ],
  );

  const tagPrintBatchOptions: TagPrintBatchOption[] = useMemo(
    () =>
      tagPrintBatches.map((batch) => ({
        metadata: batch,
        title: batch.name,
        value: batch.uuid,
      })),
    [tagPrintBatches],
  );

  return (
    <>
      <StatusBar barStyle="dark-content" />
      <PageContainer
        navigationHeaderProps={{
          BackButtonIcon: ArrowLeftThin,
          centerTitle: true,
          containerStyle: styles.background,
          onBackPress: goBack,
          showBackButton: true,
          showShadow: false,
          title: isOfflineModeActive ? (
            <View style={styles.offlineMode}>
              <OfflineModeIcon />
              <Text style={[FONTS.primary, styles.offlineModeText]}>
                {i18n.t('changes_not_saved_offline')}
              </Text>
            </View>
          ) : null,
        }}
      >
        <View
          style={[
            styles.base,
            styles.background,
            { paddingBottom: Math.max(bottom, SCREEN_PADDING_SMALL) },
          ]}
        >
          <View style={[styles.titleHeader, styles.screenPadding]}>
            <Text style={[TYPOGRAPHY.h2, FONTS.primarySemiBold]}>
              {i18n.t('batch_print_tags')}
            </Text>
          </View>
          {loading ? (
            <ActivityIndicator style={styles.loadingContainer} />
          ) : tagPrintBatchOptions.length > 0 ? (
            <RecyclerList
              contentContainerStyle={styles.screenPadding}
              estimatedItemSize={112}
              ItemSeparatorComponent={null}
              ListHeaderComponent={
                <View style={styles.createListHeaderRow}>
                  <TouchableOpacity
                    onPress={() => {
                      setShowAddBatchSheet(true);
                      Analytics.track('batch_tag_print.create_header.press', {
                        restaurantUUID,
                        supplierUUID,
                      });
                    }}
                    style={[
                      styles.createListHeader,
                      customTagPrintingEnabled
                        ? styles.createListHeaderCard
                        : null,
                    ]}
                  >
                    <CircledIcon
                      fill={theme.sk.color.icon.ondark}
                      Icon={PlusIcon}
                      iconHeight={20}
                      iconWidth={20}
                      size="compact"
                      style={styles.createListHeaderIcon}
                    />
                    <Text style={TYPOGRAPHY.h3}>{i18n.t('new_batch')}</Text>
                  </TouchableOpacity>
                  {customTagPrintingEnabled ? (
                    <TouchableOpacity
                      onPress={() => {
                        setShowCustomTagSheet(true);
                        Analytics.track(
                          'custom_tag_print.create_header.press',
                          {
                            restaurantUUID,
                            supplierUUID,
                          },
                        );
                      }}
                      style={[
                        styles.createListHeader,
                        styles.createListHeaderCard,
                      ]}
                      testID="custom-tag-button-header"
                    >
                      <CircledIcon
                        fill={null}
                        Icon={PencilIcon}
                        iconHeight={20}
                        iconWidth={20}
                        size="compact"
                        stroke={theme.sk.color.icon.ondark}
                        style={styles.createListHeaderIcon}
                      />
                      <Text style={TYPOGRAPHY.h3}>{i18n.t('custom_tag')}</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              }
              renderItem={({ item }) => (
                <TagPrintBatchListItem
                  batch={item.metadata!}
                  offlineTagBatchPrintData={offlineTagBatchPrintData}
                  updateTagPrintBatchData={updateTagPrintBatchData}
                />
              )}
              sections={[
                {
                  data: tagPrintBatchOptions,
                  key: null,
                },
              ]}
              testID="print-tag-batch-list"
            />
          ) : (
            <View style={styles.emptyState}>
              <PrinterEmptyIcon />
              <Text style={[TYPOGRAPHY.medium, styles.messageText]}>
                {i18n.t('batch_print_tags.empty_state')}
              </Text>
              <Button
                onPress={() => setShowAddBatchSheet(true)}
                style={styles.createButton}
                trackingData={{
                  name: 'batch_tag_print.create_new',
                  properties: {
                    restaurantUUID,
                    supplierUUID,
                  },
                }}
              >
                {i18n.t('new_batch')}
              </Button>
              {customTagPrintingEnabled ? (
                <Button
                  onPress={() => setShowCustomTagSheet(true)}
                  style={styles.customTagButton}
                  testID="custom-tag-button"
                  trackingData={{
                    name: 'custom_tag_print.create_new',
                    properties: {
                      restaurantUUID,
                      supplierUUID,
                    },
                  }}
                  type="secondary"
                >
                  {i18n.t('custom_tag')}
                </Button>
              ) : null}
            </View>
          )}
        </View>
        {showAddBatchSheet ? (
          <CreateTagPrintBatchActionSheet
            offlineTagBatchPrintData={offlineTagBatchPrintData}
            onClose={() => setShowAddBatchSheet(false)}
            updateTagPrintBatchData={updateTagPrintBatchData}
          />
        ) : null}
        {showCustomTagSheet ? (
          <CustomTagTemplateActionSheet
            onClose={() => setShowCustomTagSheet(false)}
          />
        ) : null}
      </PageContainer>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  background: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  base: {
    flex: 1,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  createButton: {
    borderRadius: BORDER_RADIUS_SMALL,
    marginTop: 16,
  },
  createListHeader: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: BORDER_RADIUS_SMALL,
    flex: 1,
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 16,
    paddingVertical: 36,
  },
  // Two cards side by side are too narrow for a row of icon and label
  createListHeaderCard: {
    alignItems: 'center',
    flexDirection: 'column',
    gap: 12,
    paddingVertical: 24,
  },
  createListHeaderIcon: {
    backgroundColor: theme.sk.color.bg.button.primary.default,
    borderWidth: 0,
  },
  createListHeaderRow: {
    flexDirection: 'row',
    gap: 8,
  },
  customTagButton: {
    borderRadius: BORDER_RADIUS_SMALL,
  },
  emptyState: {
    alignItems: 'center',
    flex: 1,
    gap: 12,
    justifyContent: 'center',
  },
  loadingContainer: {
    marginTop: 24,
  },
  messageText: {
    color: theme.sk.color.text.secondary,
    lineHeight: 24,
    paddingHorizontal: 24,
    textAlign: 'center',
  },
  offlineMode: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
  },
  offlineModeText: {
    color: theme.colors.primary,
  },
  screenPadding: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  titleHeader: {
    marginBottom: 24,
  },
}));
